import { Box, styled } from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { EFlightCategory } from '#shared';

import { useData } from '../../data-context';
import { getAirportLabel } from '../../helpers';
import { Taf, TafFormatError } from '../lib';

import { useAircraftDrawer } from './AircraftDrawerContext';
import { aircraftDrawerLocationClasses } from './aircraftDrawerWeatherClasses';
import { Forecast } from './Forecast';
import { MetarDetail } from './MetarDetail';

export { aircraftDrawerLocationClasses };

const Root = styled(Box)(({ theme: { spacing, palette, utils } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  color: palette.grey[700],
  [`.${aircraftDrawerLocationClasses.tabsHeader}`]: {
    fontSize: utils.clamp(14, 24),
    display: 'flex',
    gap: utils.clamp(24, 48),
    color: palette.grey[700],
    cursor: 'pointer',
    ['.active']: {
      color: 'white',
      borderBottom: '1px solid white',
    },
  },
  [`.${aircraftDrawerLocationClasses.tabLabel}`]: {},
  [`.${aircraftDrawerLocationClasses.flightCategoryHeader}`]: {
    display: 'flex',
    gap: spacing(1),
    [`.${aircraftDrawerLocationClasses.flightCategory}`]: {
      display: 'flex',
      gap: spacing(1),
      alignItems: 'center',
      padding: spacing(0, 1),
      [`.${aircraftDrawerLocationClasses.categoryIcon}`]: {
        color: 'inherit',
        background: 'currentColor',
        height: utils.clamp(12, 20),
        width: utils.clamp(12, 20),
        borderRadius: '50%',
      },
      [`.${aircraftDrawerLocationClasses.categoryLabel}`]: {},
    },
    [`.${aircraftDrawerLocationClasses.categoryTime}`]: {
      marginLeft: 'auto',
      color: palette.grey[700],
      fontSize: utils.clamp(12, 20),
    },
  },
  [`.${aircraftDrawerLocationClasses.body}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3),
    paddingBottom: spacing(2),
  },
  [`.${aircraftDrawerLocationClasses.raw}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    [`.${aircraftDrawerLocationClasses.rawItem}`]: {
      fontSize: utils.clamp(12, 20),
      background: 'none',
    },
  },
  [`.${aircraftDrawerLocationClasses.forecasts}`]: {
    display: 'flex',
    gap: spacing(2.5),
    flexDirection: 'column',
  },
  [`.flight-category-${EFlightCategory.IFR}`]: {
    color: palette.flightCategory.IFR,
    background: `${palette.flightCategory.IFR}51`,
  },
  [`.flight-category-${EFlightCategory.LIFR}`]: {
    color: palette.flightCategory.LIFR,
    background: `${palette.flightCategory.LIFR}51`,
  },
  [`.flight-category-${EFlightCategory.MVFR}`]: {
    color: palette.flightCategory.MVFR,
    background: `${palette.flightCategory.MVFR}51`,
  },
  [`.flight-category-${EFlightCategory.VFR}`]: {
    color: palette.flightCategory.VFR,
    background: `${palette.flightCategory.VFR}51`,
  },
  [`.${aircraftDrawerLocationClasses.forecast}`]: {},
  [`.${aircraftDrawerLocationClasses.forecastTime}`]: {
    color: palette.grey[700],
  },
  [`.${aircraftDrawerLocationClasses.forecastFlightCategory}`]: {},
  [`.${aircraftDrawerLocationClasses.forecastFields}`]: {
    display: 'flex',
    gap: spacing(1),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  [`.${aircraftDrawerLocationClasses.forecastField}`]: {
    display: 'flex',
    gap: spacing(2.5),
    flexDirection: 'row',
    alignItems: 'baseline',
    fontSize: utils.clamp(12, 20),
    fontFamily: 'Inter',
  },
  [`.${aircraftDrawerLocationClasses.forecastFieldLabel}`]: {
    minWidth: 180,
    textAlign: 'right',
    flexShrink: 0,
  },
  [`.${aircraftDrawerLocationClasses.forecastFieldValue}`]: {
    color: 'white',
    flexGrow: 1,
  },
}));

enum EWeatherDataType {
  'TAF' = 'TAF',
  'METAR' = 'METAR',
}

export const AircraftDrawerWeather = () => {
  const { tafsRawData, metarsData } = useData();
  const { selectedAirport } = useAircraftDrawer();
  const [selectedSource, setSelectedSource] = useState<EWeatherDataType>(
    EWeatherDataType.METAR,
  );

  const label = getAirportLabel(selectedAirport?.ICAO);

  const { taf, tafRaw } = useMemo(() => {
    const raw = selectedAirport?.ICAO
      ? tafsRawData?.[selectedAirport.ICAO]
      : undefined;
    if (!raw || !selectedAirport) {
      return { taf: undefined, tafRaw: undefined };
    }
    try {
      return {
        taf: new Taf(raw, selectedAirport.IanaTimeZone),
        tafRaw: raw,
      };
    } catch (error) {
      if (error instanceof TafFormatError) {
        // An unrecognised TAF format is an expected, recoverable condition:
        // fall back to showing the raw report, and log at `warn` so it isn't
        // captured as a Sentry error in production (captureConsole only
        // captures `error` in prod — see src/sentry.setup.ts).
        console.warn('Unsupported TAF format; showing raw report instead.', {
          icao: selectedAirport.ICAO,
          error,
        });
        return { taf: undefined, tafRaw: raw };
      }
      // Anything else (e.g. a timezone/parsing bug) is unexpected: keep it at
      // `error` so Sentry still captures it, while still degrading to raw text.
      console.error(
        'Unexpected error parsing TAF; showing raw report instead.',
        {
          icao: selectedAirport.ICAO,
          error,
        },
      );
      return { taf: undefined, tafRaw: raw };
    }
  }, [selectedAirport, tafsRawData]);
  const metar = selectedAirport?.ICAO && metarsData[selectedAirport.ICAO];

  const sourceHasData: Record<EWeatherDataType, boolean> = {
    [EWeatherDataType.METAR]: Boolean(metar),
    [EWeatherDataType.TAF]: Boolean(taf || tafRaw),
  };

  // `selectedSource` is remembered across airport switches (it is local
  // `useState`, not reset when `selectedAirport` changes), so the chosen tab
  // may point at data the airport now in view doesn't have (e.g. switching to
  // one with METAR but no TAF). Keep the user's choice while it has data;
  // otherwise fall back to whatever this airport does have, preferring METAR —
  // so the drawer never shows an empty tab (TSMED-971).
  const fallbackSource = sourceHasData[EWeatherDataType.METAR]
    ? EWeatherDataType.METAR
    : EWeatherDataType.TAF;

  const effectiveSource = sourceHasData[selectedSource]
    ? selectedSource
    : fallbackSource;

  return selectedAirport && (taf || tafRaw || metar) ? (
    <Root>
      <Box className={classNames(aircraftDrawerLocationClasses.tabsHeader)}>
        {metar ? (
          <Box
            className={classNames(aircraftDrawerLocationClasses.tabLabel, {
              active: effectiveSource === EWeatherDataType.METAR,
            })}
            onClick={() => setSelectedSource(EWeatherDataType.METAR)}
          >
            {label} METAR
          </Box>
        ) : null}
        {taf || tafRaw ? (
          <Box
            className={classNames(aircraftDrawerLocationClasses.tabLabel, {
              active: effectiveSource === EWeatherDataType.TAF,
            })}
            onClick={() => setSelectedSource(EWeatherDataType.TAF)}
          >
            {label} TAF
          </Box>
        ) : null}
      </Box>
      {metar && effectiveSource === EWeatherDataType.METAR ? (
        <Box className={classNames(aircraftDrawerLocationClasses.body)}>
          <Box
            className={classNames(
              aircraftDrawerLocationClasses.flightCategoryHeader,
            )}
          >
            <Box
              className={classNames(
                aircraftDrawerLocationClasses.flightCategory,
                {
                  [`flight-category-${metar.flightCategory}`]:
                    metar.flightCategory,
                },
              )}
            >
              <Box
                className={classNames(
                  aircraftDrawerLocationClasses.categoryIcon,
                )}
              />
              <Box
                className={classNames(
                  aircraftDrawerLocationClasses.categoryLabel,
                )}
              >
                {metar.flightCategory}
              </Box>
            </Box>
            <Box
              className={classNames(aircraftDrawerLocationClasses.categoryTime)}
            >
              {dayjs(metar.observationTime).from(dayjs())}
            </Box>
          </Box>
          <Box className={classNames(aircraftDrawerLocationClasses.raw)}>
            <Box
              className={classNames(aircraftDrawerLocationClasses.rawItem, {
                [`flight-category-${metar.flightCategory}`]:
                  metar.flightCategory,
              })}
            >
              {metar.rawData}
            </Box>
          </Box>
          <MetarDetail metar={metar} />
        </Box>
      ) : null}
      {taf && effectiveSource === EWeatherDataType.TAF ? (
        <Box className={classNames(aircraftDrawerLocationClasses.body)}>
          <Box
            className={classNames(
              aircraftDrawerLocationClasses.flightCategoryHeader,
            )}
          >
            <Box
              className={classNames(
                aircraftDrawerLocationClasses.flightCategory,
                {
                  [`flight-category-${taf.flightCategory}`]: taf.flightCategory,
                },
              )}
            >
              <Box
                className={classNames(
                  aircraftDrawerLocationClasses.categoryIcon,
                )}
              />
              <Box
                className={classNames(
                  aircraftDrawerLocationClasses.categoryLabel,
                )}
              >
                {taf.flightCategory}
              </Box>
            </Box>
            <Box
              className={classNames(aircraftDrawerLocationClasses.categoryTime)}
            >
              {taf.issueTime.from(dayjs())}
            </Box>
          </Box>
          <Box className={classNames(aircraftDrawerLocationClasses.raw)}>
            {taf.forecasts.map(forecast => (
              <Box
                key={`${forecast.changeIndicator}-${forecast.validFromString}`}
                className={classNames(aircraftDrawerLocationClasses.rawItem, {
                  [`flight-category-${forecast.flightCategory}`]:
                    forecast.flightCategory,
                })}
              >
                {forecast.rawData}
              </Box>
            ))}
          </Box>
          <Box className={aircraftDrawerLocationClasses.forecasts}>
            {taf.forecasts.map(forecast => (
              <Forecast
                key={`${forecast.changeIndicator}-${forecast.validFromString}`}
                forecast={forecast}
                timezone={selectedAirport.IanaTimeZone}
              />
            ))}
          </Box>
        </Box>
      ) : null}
      {!taf && tafRaw && effectiveSource === EWeatherDataType.TAF ? (
        <Box className={classNames(aircraftDrawerLocationClasses.body)}>
          <Box className={classNames(aircraftDrawerLocationClasses.raw)}>
            <Box className={classNames(aircraftDrawerLocationClasses.rawItem)}>
              {tafRaw}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Root>
  ) : null;
};
